import humps from 'lodash-humps-ts';
import { authAxios } from '@components/Auth';
import { User } from '@models/User';
import { snakes } from '@utils/lodash';
import { API_V1_BASE } from '@config';
import { UserProfile } from '@models/User';
import { WithParams } from '@utils/typescript';

export interface SignInWithOtpCodeAuthParams {
  mobileNumber: string;
}

export interface SignInWithPasswordAuthParams {
  mobileNumber: string;
}

export type SignInWithUsernameAuthParams = {
  username: string;
  password: string;
};

export interface RequestOtpCodeAuthParams {
  mobileNumber: string;
}

export type VerifyWithOtpCodeAuthParams = SignInWithOtpCodeAuthParams & {
  otpCode: string;
};

export type VerifyWithPasswordAuthParams = SignInWithPasswordAuthParams & {
  password: string;
};

export type UpdateUserProfileAuthParams = WithParams<{
  name: string;
  name_km: string;
  gender: string;
  dob: string;
  personal_code: string;
  occupation: string;
  email: string;
  address: string;
  height: number;
  weight: number;
  bmi: number;
}>;

export type UpdatePasswordAuthParams = WithParams<{
  password: string;
  password_confirmation: string;
  otp_code: string;
}>;

export type UpdateMobileNumberAuthParams = WithParams<{
  mobile_number: string;
  otp_code: string;
}>;

export type RequestOtpCodeForMobileNumberParams = WithParams<{
  mobile_number: string;
}>;

export class AuthApi {
  static getUserProfile = async () => {
    return await authAxios.get(`${API_V1_BASE}/user/profile`).then((response) => humps(response.data) as User);
  };

  static updateUserProfile = async (params: UpdateUserProfileAuthParams) => {
    return await authAxios
      .put(`${API_V1_BASE}/user/profile`, snakes(params))
      .then((response) => humps(response.data) as UserProfile);
  };

  static updatePassword = async (params: UpdatePasswordAuthParams) => {
    return await authAxios.put(`${API_V1_BASE}/user/password`, snakes(params));
  };

  static updateMobileNumber = async ({ mobileNumber, otpCode }: UpdateMobileNumberAuthParams) => {
    return await authAxios.put(`${API_V1_BASE}/user/${mobileNumber}`, { otp_code: otpCode });
  };

  static requestOtpCodeForMobileNumber = async ({ mobileNumber }: RequestOtpCodeForMobileNumberParams) => {
    return await authAxios.post(`${API_V1_BASE}/user/${mobileNumber}/otp_code`);
  };

  static requestUserOtpCode = async () => {
    return await authAxios.post(`${API_V1_BASE}/user/otp_code`);
  };

  static requestOtpCode = async ({ mobileNumber }: RequestOtpCodeAuthParams) => {
    return await authAxios.post(`${API_V1_BASE}/auth/otp_code/${mobileNumber}/request`);
  };

  static signInWithOtpCode = async ({ mobileNumber }: SignInWithOtpCodeAuthParams) => {
    return await authAxios
      .post(`${API_V1_BASE}/auth/otp_code/${mobileNumber}/register`)
      .then((response) => humps(response.data) as User);
  };

  static signInWithPassword = async ({ mobileNumber }: SignInWithPasswordAuthParams) => {
    return await authAxios
      .post(`${API_V1_BASE}/auth/password/${mobileNumber}/check`)
      .then((response) => humps(response.data) as User);
  };

  static verifyWithOtpCode = async ({ mobileNumber, otpCode }: VerifyWithOtpCodeAuthParams) => {
    return await authAxios
      .put(`${API_V1_BASE}/auth/otp_code/${mobileNumber}/verify`, snakes({ otpCode }))
      .then((response) => humps(response.data) as User);
  };

  static verifyWithPassword = async ({ mobileNumber, password }: VerifyWithPasswordAuthParams) => {
    return await authAxios
      .post(`${API_V1_BASE}/auth/password/${mobileNumber}/verify`, snakes({ password }))
      .then((response) => humps(response.data) as User);
  };

  static signInWithUsername = async (params: SignInWithUsernameAuthParams) => {
    return await authAxios
      .post(`${API_V1_BASE}/auth/username`, snakes(params))
      .then((response) => humps(response.data) as Pick<UserProfile, 'accessToken'>);
  };
}
